
import FinAgentMessage from '@/components/finagent/FinAgentMessage';
import FinAgentTextInputBar from '@/components/finagent/FinAgentTextInputBar';
import FinAgentSaveSnippetModal from '@/components/finagent/FinAgentSaveSnippetModal';
import FinAgentService from '@/service/FinAgentService';
import StreamQueueManager from "@/utils/streaming";
import Snackbar from '@/components/shared/SnackBar';
import { messageCreator } from "@/constants/finAgent";
import { backendExceptionHandler } from '@/mixins';
import { getAuth } from "firebase/auth";
import firebaseApp from "@/firebaseApp";
import _ from 'lodash';
import subscriptionConstants from '@/constants/subscription.constants';

const { subscribeProductKeys } = subscriptionConstants;

const auth = getAuth(firebaseApp);
const bottomSpaceAutoScroll = 50; // When the chat area is positioned about <= 50px from the bottom, we should auto scroll when has new message
const bottomSpaceUserScroll = bottomSpaceAutoScroll + 150; // The distance assumed to be a user scrolling should higher than the bottomSpaceAutoScroll

const eventStreamPrefix = {
    event: 'event: ',
    data: 'data: '
}

const eventStreamTypes = {
    message: 'message',
    suggestedFollowUpQueries: 'suggestedFollowUpQueries',
    widgets: 'widgets',
    investmentProfileDescription: 'investmentProfileDescription',
    personalityTags: 'personalityTags',
    done: 'done',
    threadTitle: 'threadTitle',
    startGeneratingWidgets: 'startGeneratingWidgets',
}
const prefixTempAgentMessageId = 'temp-agent-message-id-of-';

export default {
    name: 'FinAgentMessagesContainer',

    components: {
        FinAgentMessage,
        FinAgentTextInputBar,
        Snackbar,
        FinAgentSaveSnippetModal,
    },

    mixins: [backendExceptionHandler],

    props: {
        initMessages: {
            type: Array,
            default: () => [],
        },
        showNoDataText: {
            type: Boolean,
            default: false,
        },
        faqId: {
            type: Number,
            default: null,
        },
        isLoadingFaqAnswer: {
            type: Boolean,
            default: false,
        },
        preventAutoScroll: {
            type: Boolean,
            default: false,
        },
        allowedSending: {
            type: Boolean,
            default: true,
        },
    },

    emits: ['update-widgets-data', 'update-thread-title', 'update-menu-state', 'click-suggest-question'],

    data() {
        return {
            isCreatingNewMessage: false,
            isAtBottom: true,
            suggestQuestions: [],
            widgets: [],
            isProcessingStream: false,
            streamContent: '',
            messages: [],
            errorMessage: '',
            saveSnippetModalVisible: false,
            sippetDataToSave: {},
        };
    },

    computed: {
        showLoadingBubble() {
            return this.isProcessingStream && !this.streamContent || this.isLoadingFaqAnswer;
        },
        suggestQuestionsIconPath() {
            return this.$vuetify.theme.dark ?
                require('@/assets/icons/plane-suggestion-dark.svg') :
                require('@/assets/icons/plane-suggestion-light.svg');
        },
        chatIcon() {
            return this.$vuetify.theme.dark ?
                require('@/assets/icons/finagent-chat-icon-dark.svg') :
                require('@/assets/icons/finagent-chat-icon-light.svg');
        },
        currentThreadId() {
            return this.$store.state.finAgent.currentThread.id;
        },
        isCreatingNewThread() {
            return this.$store.state.finAgent.isCreatingNewThread;
        },
        showErrorSnackbar() {
            return !!this.errorMessage;
        },
        isFinAgentAdmin() {
            return this.$store.state.account.subscribedProductIds?.includes(subscribeProductKeys.finAgentAdmin);
        },
    },

    watch: {
        initMessages: {
            immediate: true,
            handler(items) {
                this.messages = _.cloneDeep(items);
                if (!items || !items.length) {
                    return;
                }

                this.suggestQuestions = this.getSuggestQuestions(items);
                if (!this.preventAutoScroll) {
                    this.scrollAfterInitialize(this.messages);
                }
            }
        },
        currentThreadId() {
            this.reset();
        },
    },

    created() {        
        this.messageCreator = messageCreator;
        this.finAgentService = new FinAgentService(this.$axios);
        this.streamQueue = new StreamQueueManager(3);
    },

    mounted() {
        if (this.$store.state.finAgent.preservedQuestion) {
            this.handlePreservedQuestion(this.$store.state.finAgent.preservedQuestion);
        }
    },

    beforeDestroy() {        
        this.cancelAllActiveStreams();
    },

    methods: {
        async scrollAfterInitialize(messages) {
            if (!messages.length) {
                return;
            }
            const messageIdOnUrl = this.$route.params.message_id;
            const hasValidMessage = messages.findIndex(m => m.id == messageIdOnUrl) > -1;

            if (messageIdOnUrl && !hasValidMessage) {
                this.errorMessage = this.$t('component.finAgent.error.messageNotFound', { messageId: messageIdOnUrl });
                const currentThreadId = this.$route.params.thread_id;
                const lastMessage = messages[messages.length - 1];
                const finAgentThreadRoutePath = this.$getTranslatedRoutePath(`/fin-agent/thread/${currentThreadId}/message/${lastMessage.id}/`);
                this.$router.replace({ path: finAgentThreadRoutePath });
            }

            await this.$nextTick();

            if (hasValidMessage) {
                this.scrollToMessage(messageIdOnUrl);
            } else {
                this.scrollToBottom();
            }
        },

        reset() {
            this.$store.commit('finAgent/setIsCreatingNewThread', false);
            this.isCreatingNewMessage = false;
            this.suggestQuestions = [];
            this.isProcessingStream = false;
            this.streamContent = '';
            this.isAtBottom = true;
            this.cancelAllActiveStreams();
        },

        cancelAllActiveStreams() {
            this.streamQueue.cancelAllActiveStreams();
        },

        handleScroll() {
            const chatBox = this.$refs.messagesContainer;
            if (chatBox) {
                this.isAtBottom = chatBox.scrollTop + chatBox.clientHeight >= chatBox.scrollHeight - bottomSpaceUserScroll;
            }
        },

        getSuggestQuestions(messages) {
            if (!messages || !messages.length) {
                return [];
            }

            const lastMessage = messages[messages.length - 1];            
            if (lastMessage.creator == messageCreator.user) {
                return [];
            }

            return lastMessage.suggestedFollowUpQueries || [];
        },

        async scrollToMessage(messageId) {
            await this.$nextTick();
            const messageRef =  this.$refs[`message-${messageId}`];
            if (messageRef && messageRef[0]) {
                // scroll to message and make it algin to top of chat area
                messageRef[0].$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } 
        },

        scrollToBottom(smooth = true) {
            this.$nextTick(() => {
                const container = this.$refs.messagesContainer;
                if (container) {
                    container.scrollTo({
                        top: container.scrollHeight,
                        behavior: smooth ? "smooth" : "instant"
                    });
                }
            });
        },

        handleClickSuggestQuestion(question) {
            this.$emit('click-suggest-question');
            if (this.allowedSending) {
                this.handleSendNewMessage(question);
            }
        },

        handleUpdateWidgetsData(widgets) {
            this.$emit('update-widgets-data', widgets);
        },

        handleUpdateMenuState(state) {
            this.$emit('update-menu-state', state);
        },

        async handleAutoCreateNewThread() {
            try {
                if (this.isCreatingNewThread) {
                    return;
                }
                this.$store.commit('finAgent/setIsCreatingNewThread', true);
                const createdThread = this.faqId ?
                    await this.finAgentService.createThreadFromFaq(this.faqId) :
                    await this.finAgentService.createThread();
                this.$store.commit('finAgent/addThread', createdThread);
                this.$store.commit('finAgent/setCurrentThread', createdThread);

                // is using in a component, don't need to navigate to thread page
                if (this.faqId) {
                    return;
                }

                // update the URL without reloading the page
                const url = this.$getTranslatedRoutePath(`/fin-agent/thread/${createdThread.id}/`);
                window.history.pushState({}, '', url);
            } catch (error) {
                this.handleBackendException(error,this.$t('common.errors.generalError'));
            } finally {
                this.$store.commit('finAgent/setIsCreatingNewThread', false);
            }
        },

        async addNewMessage(messageContent) {
            if (!this.currentThreadId || this.isCreatingNewMessage) {
                return;
            }

            this.isCreatingNewMessage = true;
            this.suggestQuestions = [];
            try {
                const avoidInvestmentAdvice = this.isFinAgentAdmin ? this.$store.state.preferences.finAgentInvestmentGuidelineEnabled : true;
                const avoidConsideringPortfolioData = !this.$store.state.preferences.finAgentPersonalizedEnabled;
                const createdMessage = await this.finAgentService.createMessage({
                   threadId: this.currentThreadId,
                   message: messageContent,
                   avoidInvestmentAdvice,
                   avoidConsideringPortfolioData
                });
                this.messages.push(createdMessage);

                if (this.isAtBottom) {
                    this.scrollToBottom();
                }

                this.isCreatingNewMessage = false;
                await this.fetchStream(createdMessage.id);
            } catch (error) {
                this.handleBackendException(error,this.$t('common.errors.generalError'));
                this.isCreatingNewMessage = false;
            }
        },

        generateTempAgentMessageId(userMessageId) {
            return `${prefixTempAgentMessageId}${userMessageId}`;
        },

        getUserMessageIdFromAgentMessageId(agentMessageId) {
            return agentMessageId.substring(prefixTempAgentMessageId.length);  
        },  

        updateMessageById(messageId, field, data) {
            const messageIndex = this.messages.findIndex(m => m.id === messageId);
            if (messageIndex !== -1) {
                this.$set(this.messages[messageIndex], field, data);
            }
        },

        handleSuggestedFollowUpQueries(questions, messageId) {
            this.updateMessageById(messageId, 'suggestedFollowUpQueries', [...questions]);
            this.suggestQuestions = [...questions];
        },

        handleThreadTitle(title) {
            this.$emit('update-thread-title', title)
        },

        handleWidgets(widgets, messageId) {
            const decodeWidgets = widgets.map(w => JSON.parse(w));
            this.updateMessageById(messageId, 'widgets', decodeWidgets);

            // the details section opens automatically after the answer was received
            const latestMessage = this.messages[this.messages.length - 1];
            if (messageId === latestMessage.id && latestMessage.creator === messageCreator.agent && decodeWidgets.length > 0) {
                if (this.$vuetify.breakpoint.mdAndUp) {
                    this.$emit('update-widgets-data', decodeWidgets);
                }
            }
        },

        handleMessage(data, messageId) {
            if (!this.streamContent && data.m) {
                this.messages.push({
                    id: messageId,
                    message: '',
                    creator: messageCreator.agent,
                    created: new Date().toISOString(),
                    widgets: [],
                    suggestedFollowUpQueries: [],
                    isStreaming: true,
                });
            }

            if (data.m) {
                this.streamContent += data.m;
                this.updateMessageById(messageId, 'message', this.streamContent);
            }
        },

        handleStartGeneratingWidgets() {            
            // Enable send button as widgets will be rendered asynchronously
            this.isProcessingStream = false;
            this.streamContent = '';
        },

        handleCompleteStreaming(messageId, shouldUpdateId = false) {
            // Update processing state if no other streams are active  
            if (this.streamQueue.getActiveStreams().length === 1) {
                this.isProcessingStream = false;
            }

            // Remove this stream from queue without aborting
            this.streamQueue.removeStream(messageId);

            if (!shouldUpdateId) {
                return;
            }
            const userMessageId = this.getUserMessageIdFromAgentMessageId(messageId);
            this.updateFinAgentMessageAfterStreaming(userMessageId, messageId);
        },

        updateFinAgentMessageAfterStreaming(userMessageId, agentMessageId) {
            this.finAgentService
                .pollMessage(this.currentThreadId, userMessageId)
                .then((res) => {
                    if (res?.id) {
                        this.updateMessageById(agentMessageId, 'isStreaming', false);
                        this.updateMessageById(agentMessageId, 'id', res.id);
                    }
                });
        },

        async processStream(reader, decoder, messageId) {
            const { done, value } = await reader.read();

            // Stream completed
            if (done) {
                this.streamQueue.cancelStream(messageId); 
                this.streamContent = '';
                return;
            }

            const text = decoder.decode(value);
            const lines = text.split('\n');
            let currentData = '';

            // Process each line
            for (const line of lines) {
                if (line.startsWith(eventStreamPrefix.event)) {
                    this.streamQueue.setCurrentEvent(messageId, line.slice(eventStreamPrefix.event.length));
                } else if (line.startsWith(eventStreamPrefix.data)) {
                    currentData = line.slice(eventStreamPrefix.data.length);
                    const currentEvent = this.streamQueue.getCurrentEvent(messageId);
                    // Process complete event
                    if (currentEvent && currentData) {                            
                        try {
                            const data = JSON.parse(currentData);
                            switch(currentEvent) {
                                case eventStreamTypes.message:
                                    this.handleMessage(data, messageId);
                                    break;
                                case eventStreamTypes.threadTitle:
                                    this.handleThreadTitle(data.threadTitle);
                                    break;
                                case eventStreamTypes.suggestedFollowUpQueries:                                        
                                    this.handleSuggestedFollowUpQueries(data.suggestedFollowUpQueries, messageId);
                                    break;
                                case eventStreamTypes.widgets:
                                    this.handleWidgets(data.widgets, messageId);
                                    break;
                                case eventStreamTypes.investmentProfileDescription:
                                    break;
                                case eventStreamTypes.personalityTags:
                                    break;
                                case eventStreamTypes.startGeneratingWidgets:
                                    this.handleStartGeneratingWidgets();
                                    break;
                                case eventStreamTypes.done:
                                    this.handleCompleteStreaming(messageId, true);
                                    break;
                            }
                        } catch (error) {
                            this.handleCompleteStreaming(messageId);
                            return;
                        }
                    }
                }
            }
            // Auto scroll for new messages
            const chatBox = this.$refs.messagesContainer;
            if (chatBox) {
                const isAtBottom = chatBox.scrollTop + chatBox.clientHeight >= chatBox.scrollHeight - bottomSpaceAutoScroll;
                if (isAtBottom) {
                    this.scrollToBottom();
                }
            }
            // Process next chunk
            await this.processStream(reader, decoder, messageId);
        },

        async fetchStream(userMessageId) {
            const agentMessageId = this.generateTempAgentMessageId(userMessageId);
            try {
                const controller = new AbortController();
                this.streamQueue.addStream(agentMessageId, controller);

                this.isProcessingStream = true;
                const result = await auth.currentUser.getIdTokenResult()
                let token = result.token

                if (Date.parse(result.expirationTime) < Date.now()) {
                    const res = await auth.currentUser.getIdTokenResult(true);
                    token = res.token;
                }

                // Need to use fetch because axios does not support ReadableStream
                const response = await fetch(`/api/finagent/thread/${this.currentThreadId}/messages/stream/${userMessageId}`, {
                    method: 'POST',
                    signal: controller.signal,
                    headers: {
                        'API-Authorization': `Bearer ${token}`,
                        'Accept': 'text/event-stream',
                        'Content-Type': 'application/json',
                        'Cache-Control': 'no-cache',
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || errorData.code);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();

                await this.processStream(reader, decoder, agentMessageId);
            } catch (error) {
                if (error?.name === 'AbortError') {
                    return;
                }
                this.handleCompleteStreaming(agentMessageId);
                this.errorMessage = this.$t('common.errors.generalError');
            }
        },

        async handleSendNewMessage(messageContent) {
            if (!this.currentThreadId) {
                await this.handleAutoCreateNewThread();
            }

            await this.addNewMessage(messageContent);
        },

        async handlePreservedQuestion(question) {
            await this.handleAutoCreateNewThread();

            await this.addNewMessage(question);
            this.$store.commit('finAgent/setPreservedQuestion', '');
        },

        handleErrorSnackbarEvent(event) {
            if (!event) {
                this.errorMessage = '';
            }
        },

        handleClickSaveSnippet(data) {
            this.sippetDataToSave = data;
            this.saveSnippetModalVisible = true;
        },

        handleSaveSnippetSuccess(createdSnippet) {
            const messageIndex = this.messages.findIndex(m => m.id === this.sippetDataToSave.messageId);

            if (messageIndex !== -1) {
                this.$set(this.messages[messageIndex], 'finagentLibraries', [createdSnippet]);
            }
            this.sippetDataToSave = {};
        }
    },
}
